import React, { useContext, useMemo } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ColumnsType } from "antd/es/table";
import { LanguageNameSpaces, MarketplaceSubGroup } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  getNetPnlChange,
  getPnlSortCallback,
  getRiskSortCallback,
} from "@shared/features/newMarketplace/utils";
import { GroupsSort } from "@shared/features/newMarketplace/types";
import { OPPOSITE_SORTS } from "@shared/features/newMarketplace/constants";
import Table from "common/Table/Table";
import { getTableOnChangeFunction } from "common/Table/utils";
import { SORT_ORDER_MAPPING } from "common/constants";
import Tooltip from "common/Tooltips/Tooltip";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { buildMarketPlaceGroupEvent } from "utils/analytics";
import { getGroupExtraDetailsRoute } from "utils/routes";
import { FIELD_NAME_TO_SORT } from "features/newMarketplace/common/constants";
import type { GroupsTableProps } from "features/newMarketplace/GroupsTable";
import { ScoreRing } from "features/newMarketplace/NewLook/NewLookScoreBadge";
import NewLookRiskMeter from "features/newMarketplace/NewLook/NewLookRiskMeter";
import NewLookSparkline from "features/newMarketplace/NewLook/NewLookSparkline";
import {
  GroupLogo,
  GroupRatingInline,
  GroupTagPills,
  PnlValue,
  PriceDollars,
  TrialLabel,
} from "features/newMarketplace/NewLook/NewLookGroupParts";

const SKELETON_ROWS = 6;

/**
 * The marketplace's groups as rows inside the groups panel: micro column
 * headers, 56px rows on hairlines, numbers right-aligned in their direction
 * colour, and the whole row opening the group.
 */
const NewLookGroupsTable = ({
  groups,
  loading,
  openScoreModal,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  emptyState,
}: GroupsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();
  const skeleton = !!loading && !groups.length;

  const sortOrder = (sort: GroupsSort) =>
    !skeleton && sortBy === sort
      ? SORT_ORDER_MAPPING[sortDirection]
      : undefined;
  const pnl = (group: MarketplaceSubGroup, timeframe: 30 | 90 | 180) =>
    skeleton ? (
      <Skeleton width={52} height={12} style={{ marginLeft: "auto" }} />
    ) : (
      <PnlValue
        value={getNetPnlChange(group, timeframe, displayCurrency)}
        role="figure"
      />
    );

  const columns: ColumnsType<MarketplaceSubGroup> = [
    {
      title: t("table.columns.score"),
      dataIndex: "backtesting_score",
      width: 76,
      defaultSortOrder: "descend",
      sortOrder: sortOrder(GroupsSort.SCORE),
      sorter: (a, b) => sortByNumberProperty(a, b, "backtesting_score"),
      render: (_, group) =>
        skeleton ? (
          <Skeleton width={36} height={36} radius={18} />
        ) : (
          <ScoreRing
            score={group.backtesting_score}
            onClick={() => openScoreModal?.(group)}
          />
        ),
    },
    {
      title: t("table.columns.group_name"),
      dataIndex: "name",
      width: "32%",
      sortOrder: sortOrder(GroupsSort.NAME),
      sorter: (a, b) => sortByStringProperty(a, b, "name"),
      render: (_, group) =>
        skeleton ? (
          <NameCell>
            <Skeleton width={32} height={32} radius={8} />
            <Skeleton width={140} height={12} />
          </NameCell>
        ) : (
          <NameCell>
            <GroupLogo image={group.image_path} name={group.name} size={36} />
            <Names>
              <Tooltip title={group.name} placement="topLeft">
                <Name>{group.name}</Name>
              </Tooltip>
              <MetaLine>
                <GroupRatingInline
                  avgStars={group.avg_stars}
                  numReviewers={group.num_of_reviewers}
                />
                <GroupTagPills group={group} />
              </MetaLine>
            </Names>
          </NameCell>
        ),
    },
    {
      title: t("table.columns.price"),
      dataIndex: "price_number",
      width: 128,
      sortOrder: sortOrder(GroupsSort.PRICE),
      sorter: (a, b) => sortByNumberProperty(a, b, "price_number"),
      render: (_, group) =>
        skeleton ? (
          <Stack>
            <Skeleton width={40} height={12} />
            <Skeleton width={64} height={10} />
          </Stack>
        ) : (
          <Stack>
            <PriceDollars priceNumber={group.price_number} />
            <TrialLabel
              hasFreeTrial={group.has_free_trial}
              isFree={group.is_free}
            />
          </Stack>
        ),
    },
    {
      title: t("table.columns.pnl_6m"),
      dataIndex: "timeframe_to_backtesting_run_data.180",
      align: "right",
      width: 104,
      sortOrder: sortOrder(GroupsSort.SIX_MONTH_PNL),
      sorter: getPnlSortCallback(180, displayCurrency),
      render: (_, group) => pnl(group, 180),
    },
    {
      title: t("table.columns.pnl_3m"),
      dataIndex: "timeframe_to_backtesting_run_data.90",
      align: "right",
      width: 104,
      sortOrder: sortOrder(GroupsSort.THREE_MONTH_PNL),
      sorter: getPnlSortCallback(90, displayCurrency),
      render: (_, group) => pnl(group, 90),
    },
    {
      title: t("table.columns.one_month_pnl"),
      dataIndex: "timeframe_to_backtesting_run_data.30",
      align: "right",
      width: 184,
      sortOrder: sortOrder(GroupsSort.ONE_MONTH_PNL),
      sorter: getPnlSortCallback(30, displayCurrency),
      render: (_, group) => (
        <TrendCell>
          {skeleton ? (
            <Skeleton width={72} height={20} />
          ) : (
            <NewLookSparkline group={group} />
          )}
          {pnl(group, 30)}
        </TrendCell>
      ),
    },
    {
      title: t("table.columns.risk"),
      dataIndex: "backtesting_score_breakdown",
      width: 140,
      sortOrder: sortOrder(GroupsSort.RISK),
      sorter: getRiskSortCallback(),
      render: (scoreBreakdown) =>
        skeleton ? (
          <Skeleton width={80} height={12} />
        ) : (
          <NewLookRiskMeter scoreBreakdown={scoreBreakdown} />
        ),
    },
  ];

  const placeholders = useMemo(
    () =>
      Array.from(
        { length: SKELETON_ROWS },
        (_, index) => ({ group_id: -index - 1 } as MarketplaceSubGroup)
      ),
    []
  );

  const onGroupClick = (group: MarketplaceSubGroup, index: number) => {
    analytics?.marketplaceGroupClicked(
      buildMarketPlaceGroupEvent(group, index + 1)
    );
    history.push(
      getGroupExtraDetailsRoute(location.pathname, group.group_id.toString())
    );
  };

  const handleChange = getTableOnChangeFunction<GroupsSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT,
    OPPOSITE_SORTS
  );

  return (
    <Frame aria-busy={skeleton || undefined}>
      <Table<MarketplaceSubGroup>
        columns={columns}
        dataSource={skeleton ? placeholders : groups}
        rowKey={(group) => group.group_id}
        locale={{ emptyText: emptyState }}
        pagination={{ showSizeChanger: false, hideOnSinglePage: true }}
        onRow={(group, index) =>
          skeleton
            ? {}
            : {
                tabIndex: 0,
                onClick: () => onGroupClick(group, index ?? 0),
                onKeyDown: (event) => {
                  if (
                    event.key === "Enter" &&
                    event.target === event.currentTarget
                  )
                    onGroupClick(group, index ?? 0);
                },
              }
        }
        showSorterTooltip={false}
        onChange={handleChange}
      />
    </Frame>
  );
};

export default NewLookGroupsTable;

const Frame = styled.div`
  && .ant-table-wrapper .ant-table,
  && .ant-table-wrapper .ant-table-container {
    background: transparent;
  }
  && .ant-table-thead > tr > th {
    height: 36px;
    padding: 0 12px;
    ${newLookType.micro};
    color: ${(p) => getNewLookTokens(p.theme).muted};
    background: transparent !important;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  && .ant-table-thead .ant-table-column-title {
    color: inherit;
  }
  && .ant-table-thead > tr > th.ant-table-column-sort {
    color: ${(p) => getNewLookTokens(p.theme).secondary};
  }
  && .ant-table-column-sorter {
    color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  }
  && .ant-table-tbody > tr > td {
    height: 64px;
    padding: 10px 12px;
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    background: transparent !important;
    color: ${(p) => getNewLookTokens(p.theme).text};
    transition: background-color 120ms;
  }
  && .ant-table-thead > tr > th:first-child,
  && .ant-table-tbody > tr > td:first-child {
    padding-left: 24px;
  }
  && .ant-table-thead > tr > th:last-child,
  && .ant-table-tbody > tr > td:last-child {
    padding-right: 24px;
  }
  && .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  && .ant-table-tbody > tr.ant-table-row {
    cursor: pointer;
    outline: none;
  }
  && .ant-table-tbody > tr.ant-table-row:hover > td {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
  }
  && .ant-table-tbody > tr.ant-table-row:focus-visible > td {
    background: ${(p) => getNewLookTokens(p.theme).rowHover} !important;
    box-shadow: inset 0 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  && .ant-table-tbody > tr.ant-table-row:focus-visible > td:first-child {
    box-shadow: inset 2px 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  && .ant-table-tbody > tr.ant-table-row:focus-visible > td:last-child {
    box-shadow: inset -2px 2px 0 ${(p) => getNewLookTokens(p.theme).focus},
      inset 0 -2px 0 ${(p) => getNewLookTokens(p.theme).focus};
  }
  && .ant-table-tbody > tr.ant-table-placeholder > td {
    height: auto;
    cursor: default;
  }
  && .ant-table-tbody > tr.ant-table-placeholder:hover > td {
    background: transparent !important;
  }
  && .ant-table-wrapper .ant-pagination {
    margin: 0;
    padding: 12px 20px;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;
const Names = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
`;
const Name = styled.div`
  max-width: 100%;
  font-size: 15px;
  line-height: 20px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MetaLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-width: 0;
`;
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;
const TrendCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
`;
